<template>
  <div class="preview-container">
    <div class="preview-header">
      <div class="header-left">
        <button @click="goBack" class="btn-back"><i class="fas fa-arrow-left"></i> 返回</button>
        <h3>{{ docTitle }}</h3>
      </div>
      <div class="header-right">
        <span class="doc-info">{{ docType.toUpperCase() }} | 版本 v{{ docVersion }}</span>
        <button v-if="canDownload" @click="downloadDoc" class="btn-download">
          <i class="fas fa-download"></i> 下载
        </button>
      </div>
    </div>

    <div class="preview-body" ref="previewBody">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>正在加载文档...</p>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ errorMessage }}</p>
      </div>

      <!-- PDF 预览 -->
      <div v-else-if="docType === 'pdf'" class="pdf-viewer">
        <iframe :src="previewUrl" frameborder="0"></iframe>
      </div>

      <!-- Office 文档预览 (Word, Excel, PPT) -->
      <div v-else-if="isOfficeDoc" class="office-viewer">
        <iframe :src="officePreviewUrl" frameborder="0"></iframe>
      </div>

      <!-- 文本文件预览 -->
      <div v-else-if="docType === 'txt'" class="text-viewer">
        <pre>{{ textContent }}</pre>
      </div>

      <!-- 图片预览 -->
      <div v-else-if="isImageDoc" class="image-viewer">
        <img :src="previewUrl" :alt="docTitle" />
      </div>

      <!-- 不支持的格式 -->
      <div v-else class="unsupported-state">
        <i class="fas fa-file"></i>
        <p>该文件格式不支持在线预览</p>
        <button v-if="canDownload" @click="downloadDoc" class="btn btn-primary">
          <i class="fas fa-download"></i> 下载查看
        </button>
      </div>

      <!-- 水印层 -->
      <div class="watermark-layer" ref="watermarkLayer"></div>
    </div>

    <!-- 禁用右键菜单和选择 -->
    <div v-if="!canDownload" class="protection-overlay"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getFileDetail, getFilePermissions } from '../api/document'
import { getUser } from '../utils/sessionManager'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const error = ref(false)
const errorMessage = ref('')
const docTitle = ref('')
const docType = ref('')
const docVersion = ref('1.0')
const previewUrl = ref('')
const officePreviewUrl = ref('')
const textContent = ref('')
const canDownload = ref(false)
const fileInfo = ref(null)
const previewBody = ref(null)
const watermarkLayer = ref(null)

const user = getUser()
const watermarkText = computed(() => {
  const userName = user?.name || user?.account || '未知用户'
  const now = new Date()
  const dateStr = now.toLocaleDateString('zh-CN')
  const timeStr = now.toLocaleTimeString('zh-CN')
  return `${userName} ${dateStr} ${timeStr}`
})

const isOfficeDoc = computed(() => {
  const type = docType.value.toLowerCase().replace(/^\./, '')
  return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(type)
})
const isImageDoc = computed(() => {
  const type = docType.value.toLowerCase().replace(/^\./, '')
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(type)
})

const goBack = () => {
  router.back()
}

const downloadDoc = () => {
  if (!canDownload.value) {
    window.$message.warning('您没有下载权限')
    return
  }
  const baseUrl = window.__APP_CONFIG__?.API_BASE_URL || 'http://localhost:5260/api'
  const params = new URLSearchParams({
    guid: fileInfo.value.Guid || fileInfo.value.guid,
    title: fileInfo.value.Titles || fileInfo.value.titles,
    savePath: fileInfo.value.SavePath || fileInfo.value.savePath || ''
  })
  const url = `${baseUrl}/document/files/download?${params.toString()}`
  window.open(url, '_blank')
}

/**
 * 加载文档信息和权限
 */
const loadDocumentInfo = async () => {
  try {
    loading.value = true
    const guid = route.query.guid

    console.log('=== 文档预览调试信息 ===')
    console.log('1. 当前路由query:', route.query)
    console.log('2. 文档GUID:', guid)

    if (!guid) {
      throw new Error('缺少文档ID')
    }

    console.log('3. 开始获取文档信息...')
    // 获取文档信息
    const response = await getFileDetail(guid)
    console.log('4. 文档信息响应:', response)
    fileInfo.value = response.data

    if (!fileInfo.value) {
      throw new Error('文档不存在')
    }

    docTitle.value = fileInfo.value.Titles || fileInfo.value.titles
    docType.value = (fileInfo.value.ContentType || fileInfo.value.contentType || '').toLowerCase()
    docVersion.value = fileInfo.value.Vesion || fileInfo.value.vesion || '1.0'

    console.log('5. 文档信息:', { title: docTitle.value, type: docType.value, version: docVersion.value })

    // 获取权限
    try {
      console.log('6. 开始获取权限...')
      const permResponse = await getFilePermissions(guid)
      console.log('7. 权限响应:', permResponse)
      if (permResponse.data) {
        canDownload.value = permResponse.data.canDownload || false
      }
    } catch (permError) {
      console.warn('获取权限失败，使用默认权限:', permError)
      // 如果权限接口失败，回退到Para1字段
      // Para1字段第5位(索引4)表示下载权限
      const permissions = fileInfo.value.Para1 || fileInfo.value.para1 || '00000'
      canDownload.value = permissions.length > 4 && permissions[4] === '1'
    }

    console.log('8. 下载权限:', canDownload.value)

    // 构建预览URL
    await buildPreviewUrl()

    console.log('9. 预览URL已构建')
    loading.value = false

    // 渲染水印
    setTimeout(() => {
      renderWatermark()
      console.log('10. 水印已渲染')
    }, 500)
  } catch (err) {
    console.error('❌ 加载文档失败:', err)
    error.value = true
    errorMessage.value = err.message || '加载文档失败'
    loading.value = false
  }
}

/**
 * 构建预览URL
 */
const buildPreviewUrl = async () => {
  const baseUrl = window.__APP_CONFIG__?.API_BASE_URL || 'http://localhost:5260/api'
  const guid = fileInfo.value.Guid || fileInfo.value.guid
  
  // 清理文件类型,移除可能的扩展名
  let cleanType = docType.value
  if (cleanType && cleanType.startsWith('.')) {
    cleanType = cleanType.substring(1)
  }
  cleanType = cleanType.toLowerCase()

  console.log('构建预览URL - 基础信息:', { baseUrl, guid, originalType: docType.value, cleanType })

  switch (cleanType) {
    case 'pdf':
      // PDF使用浏览器内置预览
      previewUrl.value = `${baseUrl}/document/files/preview?guid=${encodeURIComponent(guid)}&type=pdf`
      console.log('PDF预览URL:', previewUrl.value)
      break

    case 'doc':
    case 'docx':
    case 'xls':
    case 'xlsx':
    case 'ppt':
    case 'pptx':
      // Office文档使用Office Online Viewer (Microsoft)
      const encodedUrl = encodeURIComponent(`${baseUrl}/document/files/preview?guid=${encodeURIComponent(guid)}`)
      officePreviewUrl.value = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`
      console.log('Office预览URL:', officePreviewUrl.value)
      break

    case 'txt':
      // 文本文件直接读取内容
      try {
        const textUrl = `${baseUrl}/document/files/preview?guid=${encodeURIComponent(guid)}&type=text`
        console.log('文本预览URL:', textUrl)
        const response = await fetch(textUrl)
        textContent.value = await response.text()
        console.log('文本内容加载成功，长度:', textContent.value.length)
      } catch (err) {
        console.error('加载文本内容失败:', err)
        textContent.value = '加载文本内容失败'
      }
      break

    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
      // 图片直接显示
      previewUrl.value = `${baseUrl}/document/files/preview?guid=${encodeURIComponent(guid)}`
      console.log('图片预览URL:', previewUrl.value)
      break

    default:
      console.warn('不支持的文件类型:', cleanType)
  }
}

/**
 * 渲染水印
 */
const renderWatermark = () => {
  if (!watermarkLayer.value) return

  const container = watermarkLayer.value
  container.innerHTML = ''

  const watermarkCount = 50 // 水印数量
  const containerWidth = container.offsetWidth
  const containerHeight = container.offsetHeight

  for (let i = 0; i < watermarkCount; i++) {
    const mark = document.createElement('div')
    mark.className = 'watermark-item'
    mark.textContent = watermarkText.value
    
    // 随机位置
    mark.style.left = Math.random() * containerWidth + 'px'
    mark.style.top = Math.random() * containerHeight + 'px'
    mark.style.transform = `rotate(-30deg)`
    
    container.appendChild(mark)
  }
}

/**
 * 禁止复制和截图
 */
const preventCopyAndScreenshot = () => {
  if (!canDownload.value) {
    // 禁用右键菜单
    document.addEventListener('contextmenu', handleContextMenu)
    
    // 禁用选择
    document.addEventListener('selectstart', handleSelectStart)
    
    // 禁用复制
    document.addEventListener('copy', handleCopy)
    
    // 禁用打印
    window.onbeforeprint = handleBeforePrint
    
    // 禁用截图快捷键
    document.addEventListener('keydown', handleKeyDown)
  }
}

const handleContextMenu = (e) => {
  e.preventDefault()
  return false
}

const handleSelectStart = (e) => {
  e.preventDefault()
  return false
}

const handleCopy = (e) => {
  e.preventDefault()
  return false
}

const handleBeforePrint = () => {
  window.$message.warning('该文档禁止打印')
  return false
}

const handleKeyDown = (e) => {
  // 禁用 Ctrl+P (打印), PrtSc (截图), Ctrl+S (保存)
  if ((e.ctrlKey && e.key === 'p') || 
      (e.ctrlKey && e.key === 's') || 
      e.key === 'PrintScreen') {
    e.preventDefault()
    return false
  }
}

/**
 * 清理事件监听
 */
const cleanup = () => {
  document.removeEventListener('contextmenu', handleContextMenu)
  document.removeEventListener('selectstart', handleSelectStart)
  document.removeEventListener('copy', handleCopy)
  document.removeEventListener('keydown', handleKeyDown)
  window.onbeforeprint = null
}

onMounted(() => {
  loadDocumentInfo()
  preventCopyAndScreenshot()
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.preview-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.preview-header {
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #e8ecf1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  background: none;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #374151;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-back:hover {
  background: #f9fafb;
  border-color: #3b82f6;
  color: #3b82f6;
}

.preview-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.doc-info {
  font-size: 13px;
  color: #6b7280;
}

.btn-download {
  background: #3b82f6;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-download:hover {
  background: #2563eb;
}

.preview-body {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.loading-state,
.error-state,
.unsupported-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
}

.loading-state i {
  font-size: 48px;
  color: #3b82f6;
  margin-bottom: 16px;
}

.error-state i,
.unsupported-state i {
  font-size: 48px;
  color: #ef4444;
  margin-bottom: 16px;
}

.unsupported-state i {
  color: #9ca3af;
}

.pdf-viewer iframe,
.office-viewer iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.text-viewer {
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 20px;
  background: #fff;
}

.text-viewer pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  color: #1f2937;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.image-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  overflow: auto;
}

.image-viewer img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 水印层 */
.watermark-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
}

.watermark-item {
  position: absolute;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.08);
  white-space: nowrap;
  user-select: none;
  pointer-events: none;
}

/* 保护层 - 禁用下载时覆盖 */
.protection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
  user-select: none;
  pointer-events: none;
}

.btn {
  padding: 10px 20px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #3b82f6;
  color: #fff;
}

.btn-primary:hover {
  background: #2563eb;
}
</style>
